import posthog from "posthog-js";
import { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import useAlert from "hooks/useAlert";
import { Button } from "components/atoms/Button";
import { EditIcon } from "components/atoms/Icon";
import { Header } from "components/atoms/Typography";
import { ResetButton } from "components/molecules/DangerousButtons";
import { useDeleteEquipmentData } from "mutations/useDeleteEquipmentSurvey";
import { useParams } from "react-router-dom";
import useBuildingLoadingState from "recoilStore/useBuildingLoadingState";
import { useQueryOnStatusUpdate } from "pages/BuildingPage/BuildingSidebar/BuildingEnergy.hooks";

interface BuildingEquipmentHeaderProps {
    editSurveyWizard: () => void;
    editSurveySingleForm: () => void;
    buildingName: string;
}

function BuildingEquipmentHeader({
    editSurveyWizard,
    editSurveySingleForm,
    buildingName,
}: BuildingEquipmentHeaderProps) {
    const alert = useAlert();
    const [editMenuAnchor, setEditMenuAnchor] = useState<null | HTMLElement>(null);

    const resetButtonOptions = {
        popup: {
            buttonText: "Reset survey",
            icon: <EditIcon />,
        },
        modal: {
            title: "Reset survey",
            body: (
                <>
                    This will{" "}
                    <span style={{ fontWeight: "bold" }}>
                        permanently remove all equipment data associated with the {buildingName} building
                    </span>
                    . Do you wish to proceed?
                </>
            ),
            deleteButtonText: "Reset survey",
        },
    };

    const { buildingModelUid } = useParams();
    if (!buildingModelUid) throw new Error("buildingModelUid should be defined");

    const { setLoadingState } = useBuildingLoadingState(buildingModelUid);
    const { onSuccess, onError } = useQueryOnStatusUpdate(buildingModelUid);
    const { mutate: deleteEquipmentData } = useDeleteEquipmentData(
        buildingModelUid,
        () => {
            onSuccess();
            posthog.capture("Equipment reset");
            alert.success("Equipment information reset.");
        },
        onError
    );

    const handleDeleteEquipmentData = () => {
        setLoadingState((prev) => ({
            ...prev,
            equipmentSidebarLoading: true,
            buildingReportsLoading: true,
        }));
        deleteEquipmentData();
    };

    return (
        <div className="building-equipment-header">
            <Header>Equipment</Header>
            <Button
                icon={<EditIcon color="var(--audette-black)" width="1rem" height="1rem" />}
                onClick={(event) => setEditMenuAnchor(event.currentTarget)}
                type="secondary"
                dataTestId="edit-equipment-button"
            >
                Edit
            </Button>
            <Menu anchorEl={editMenuAnchor} open={Boolean(editMenuAnchor)} onClose={() => setEditMenuAnchor(null)}>
                <MenuItem
                    data-testid="edit-equipment-wizard-option"
                    onClick={() => {
                        setEditMenuAnchor(null);
                        editSurveyWizard();
                        posthog.capture("Edit-equipment wizard started");
                    }}
                >
                    Edit with wizard
                </MenuItem>
                <MenuItem
                    data-testid="edit-equipment-single-form-option"
                    onClick={() => {
                        setEditMenuAnchor(null);
                        editSurveySingleForm();
                        posthog.capture("Edit-equipment single-form started");
                    }}
                >
                    Edit with single form
                </MenuItem>
            </Menu>
            <ResetButton options={resetButtonOptions} onClick={() => handleDeleteEquipmentData()} />
        </div>
    );
}

export default BuildingEquipmentHeader;
